import { useApiFetch } from '@/services/api-client';
import { useAuthStore } from '@/stores/auth-store';
import { getCurrentRouteName } from './current-route';

export interface AnalyticsProvider {
   trackEvent: (eventName: string, properties: Record<string, unknown>) => void;
}

const GOOGLE_ANALYTICS_SUPPRESSED_EVENTS = ['page:view']; // page:view is automatic to google
export class GoogleAnalyticsProvider implements AnalyticsProvider {
   trackEvent(eventName: string, properties: Record<string, unknown>): void {
      try {
         if (GOOGLE_ANALYTICS_SUPPRESSED_EVENTS.includes(eventName as AnalyticEvent))
            return;
         // colon is not allowed in GA4 event names, so convert to double underscore
         const sanitizedEventName = eventName.replace(/:/g, '__');
         window.gtag('event', sanitizedEventName, properties);
      }
      catch {
         // Silently swallow — gtag may be blocked by ad-blockers
      }
   }
}

export class MixpanelProvider implements AnalyticsProvider {
   trackEvent(eventName: string, properties: Record<string, unknown>): void {
      try {
         const mp = 'mixpanel' in window ? window.mixpanel as { track: (eventName: string, properties: Record<string, unknown>) => void } : undefined;
         if (!mp)
            return;
         mp.track(eventName, properties);
      }
      catch {
         // Silently swallow — mixpanel may be blocked by ad-blockers
      }
   }
}

export class ApalumaBackendProvider implements AnalyticsProvider {
   trackEvent(eventName: string, properties: Record<string, unknown>): void {
      try {
         switch (eventName) {
            case 'page:view':
               useApiFetch('analytics/user-activity', {
                  method: 'POST',
                  query: { source: 'web' },
               }).catch(() => {});
               break;

            case 'chat:feedback':
               useApiFetch('nmed/chatbot/feedback/', {
                  method: 'POST',
                  body: {
                     bot_response: properties.bot_response,
                     feedback: properties.feedback,
                     is_positive: properties.is_positive,
                     question: properties.question,
                  },
               }).catch(() => {});
               break;

            default:
               // No backend endpoint for this event — no-op.
               // Note: document:report intentionally falls through here.
               // Its backend POST is owned by modal-report-doc.vue so the
               // modal can read `team_name` off the response for its
               // per-tenant confirmation; this provider only handles the
               // analytics side via GA / Mixpanel.
               break;
         }
      }
      catch {
         // Fire-and-forget — silently swallow network errors
      }
   }
}

const providers: AnalyticsProvider[] = [
   new GoogleAnalyticsProvider(),
   new ApalumaBackendProvider(),
   new MixpanelProvider(),
];

/**
 * Where a question was asked, set by whichever component owns the input.
 *
 * Named for the person reading the report rather than after the component that
 * emits it. `chat_page` is the full-screen thread; `chat_drawer` is the Luma
 * drawer, which is reachable from every screen, so it is only legible next to
 * the `page` property recording what was underneath it.
 */
export type ChatSurface
   = | 'chat_page'
      | 'chat_drawer'
      | 'search_bar'
      | 'document'
      | 'project';

/**
 * What was clicked to open a document.
 *
 * `chat_citation` is the one that answers whether Luma leads anywhere, and it
 * cannot be told from `browse_listing` by component alone: the same table
 * renders both, so that one is read from state at the moment of the click.
 */
export type DocumentOpenOrigin
   = | 'search_results'
      | 'chat_citation'
      | 'browse_listing'
      | 'project'
      | 'facility'
      | 'saved_docs';

// events must be registered in Google Analytics. Allowlisting them here keeps us honest.
type SilencedEvent = 'page:view'; // Apaluma Analytics does this one manually, but it's automatic elsewhere.
type ProjectEvent = 'project:add_file' | 'project:remove_file' | 'project:chat' | 'project:open';
type DocumentEvent = 'document:open' | 'document:report' | 'document:chat';
type ChatEvent = 'chat:ask' | 'chat:feedback' | 'chat:reply';
type SearchEvent = 'search:browse_results' | 'search:execute';
type ExportEvent = 'metadata:export' | 'zip:export';
type AuthEvent = 'auth:signup' | 'auth:login';
type AnalyticEvent = SilencedEvent | ChatEvent | DocumentEvent | ProjectEvent | SearchEvent | ExportEvent | AuthEvent | 'mappin:open';

export function useAnalytics() {
   function trackEvent(eventName: AnalyticEvent, properties: Record<string, unknown> = {}) {
      const authStore = useAuthStore();
      // auth:* events fire around the moment of authentication (e.g. signup happens
      // before the user is signed in), so they must not be gated on isAuthenticated.
      const isAuthEvent = eventName.startsWith('auth:');
      if (!isAuthEvent && !authStore.isAuthenticated)
         return;

      const enrichedProperties = {
         ...properties,
         user_id: authStore.accessTokenObject?.sub ?? '',
         organization: authStore.userProfile?.organization ?? '',
         // The route on screen at the moment of the call, so an action reports
         // where it happened rather than where it was defined.
         //
         // The settled route is right for everything that acts in place, and
         // wrong for anything that navigates before it reports: it would name
         // the destination. Those emitters read the route first and pass it in,
         // and it wins here, because an emitter that navigated knows something
         // the router no longer does.
         //
         // Not `page`: that name is already taken by the pagination number on
         // `search:browse_results`, and this object is spread last, so reusing
         // it would silently overwrite which page of results the user was on.
         screen: properties.screen ?? getCurrentRouteName(),
      };

      for (const provider of providers) {
         try {
            provider.trackEvent(eventName, enrichedProperties);
         }
         catch {
            // Silently swallow — ad-blockers, network issues, etc.
         }
      }
   }

   async function trackOperation<T>(
      eventName: AnalyticEvent,
      properties: Record<string, unknown>,
      operation: () => Promise<T>,
   ): Promise<T> {
      try {
         const result = await operation();
         trackEvent(eventName, { ...properties, outcome: 'ok' });
         return result;
      }
      catch (e) {
         trackEvent(eventName, { ...properties, outcome: 'error' });
         throw e;
      }
   }

   return { trackEvent, trackOperation };
}
